import { ApiError, SessionContext, StepResult, type StepType } from '@esign/contracts'
import { middlewareApi, type MiddlewareRequestOptions } from './middleware-api.js'
import { DEMO_SESSION_ID, markDemoSigned } from './demo-session.js'
import { apiUrl, expandPath, frontendConfig } from './config.js'

export class SigningApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(apiUrl(path), {
    credentials: 'include',
    cache: 'no-store',
    ...init,
    headers: { accept: 'application/json', ...init?.headers },
  })
  const body: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const parsed = ApiError.safeParse(body)
    throw new SigningApiError(
      parsed.success ? parsed.data.message : 'Không thể kết nối dịch vụ ký hợp đồng.',
      parsed.success ? parsed.data.error_code : 'HTTP_ERROR',
      response.status,
    )
  }
  return body
}

function dataUrlToFile(dataUrl: string, filename: string): File | Blob {
  try {
    const arr = dataUrl.split(',')
    const mimeMatch = arr[0]?.match(/:(.*?);/)
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'
    const bstr = atob(arr[1] || '')
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, { type: mime })
  } catch {
    return new Blob([], { type: 'image/jpeg' })
  }
}

function sanitizeImageUrl(url: unknown, fallback: string): string {
  if (typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://')) && url.length <= 500) {
    return url
  }
  return fallback
}

function ensureHorizontalImage(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !dataUrl.startsWith('data:image/')) {
      resolve(dataUrl)
      return
    }
    const img = new Image()
    img.onload = () => {
      if (img.naturalHeight > img.naturalWidth) {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = img.naturalHeight
          canvas.height = img.naturalWidth
          const ctx = canvas.getContext('2d')
          if (ctx) {
            ctx.translate(canvas.width / 2, canvas.height / 2)
            ctx.rotate((270 * Math.PI) / 180)
            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2)
            resolve(canvas.toDataURL('image/jpeg', 0.95))
            return
          }
        } catch {
          // ignore
        }
      }
      resolve(dataUrl)
    }
    img.onerror = () => resolve(dataUrl)
    img.src = dataUrl
  })
}

async function resolveImageUrl(
  imageSource: unknown,
  filename: string,
  options?: MiddlewareRequestOptions,
  fallback = 'https://cdn.oneat.org/ekyc/front.jpg',
): Promise<string> {
  if (typeof imageSource !== 'string' || !imageSource) {
    return fallback
  }

  // Nếu đã là HTTP/HTTPS URL hợp lệ và không phải domain mock
  if (
    (imageSource.startsWith('http://') || imageSource.startsWith('https://')) &&
    !imageSource.includes('cdn.oneat.org') &&
    imageSource.length <= 500
  ) {
    return imageSource
  }

  // Nếu là base64 data URI hoặc chuỗi base64 thô dài
  const isBase64 =
    imageSource.startsWith('data:image/') ||
    imageSource.startsWith('data:application/') ||
    (!imageSource.startsWith('http://') && !imageSource.startsWith('https://') && imageSource.length > 50)

  if (isBase64) {
    try {
      const normalizedDataUrl = imageSource.startsWith('data:')
        ? imageSource
        : `data:image/jpeg;base64,${imageSource}`
      const horizontalDataUrl = await ensureHorizontalImage(normalizedDataUrl)
      const file = dataUrlToFile(horizontalDataUrl, filename)
      const uploadRes: any = await middlewareApi.upload(file, options)
      if (uploadRes?.data?.url && typeof uploadRes.data.url === 'string') {
        console.log(`[EKYC Upload] Uploaded ${filename} successfully:`, uploadRes.data.url)
        return uploadRes.data.url
      }
    } catch (err) {
      console.warn(`[EKYC Upload] Không thể upload ${filename}:`, err)
    }
  }

  return sanitizeImageUrl(imageSource, fallback)
}

export async function fetchSessionContext(sessionId: string): Promise<SessionContext> {
  const path = expandPath(frontendConfig().contextPath, { sessionId })
  return SessionContext.parse(await request(path))
}

function persistEkycSdkFromInit(
  sessionId: string,
  data: Record<string, any> | undefined,
  identity: Record<string, unknown>,
): void {
  if (typeof sessionStorage === 'undefined') return

  if (data?.ekyc_sdk_url) sessionStorage.setItem('dev_ekyc_sdk_url', data.ekyc_sdk_url)
  if (data?.ekyc_sdk_token) {
    sessionStorage.setItem('dev_ekyc_sdk_token', data.ekyc_sdk_token)
    sessionStorage.setItem(`dev_ekyc_sdk_token_${sessionId}`, data.ekyc_sdk_token)
  }
  if (data?.ekyc_sdk_token_name) {
    sessionStorage.setItem('dev_ekyc_sdk_token_name', data.ekyc_sdk_token_name)
    sessionStorage.setItem(`dev_ekyc_sdk_token_name_${sessionId}`, data.ekyc_sdk_token_name)
  }
  if (data?.ekyc_sdk_access_token) {
    sessionStorage.setItem('dev_ekyc_sdk_access_token', data.ekyc_sdk_access_token)
    sessionStorage.setItem('dev_ekyc_access_token', data.ekyc_sdk_access_token)
    sessionStorage.setItem(`dev_ekyc_sdk_access_token_${sessionId}`, data.ekyc_sdk_access_token)
  }
  if (data?.ekyc_sdk_resource_id) {
    sessionStorage.setItem('dev_ekyc_sdk_resource_id', data.ekyc_sdk_resource_id)
    sessionStorage.setItem(`dev_ekyc_sdk_resource_id_${sessionId}`, data.ekyc_sdk_resource_id)
  }

  const isDemo = sessionId === DEMO_SESSION_ID
  const prevIdentityStr =
    sessionStorage.getItem(`dev_identity_data_${sessionId}`) ||
    (isDemo ? sessionStorage.getItem('dev_identity_data') : null)
  let prevIdentity = {}
  if (prevIdentityStr) {
    try {
      prevIdentity = JSON.parse(prevIdentityStr)
    } catch {
      // ignore
    }
  }
  const updated = { ...prevIdentity, ...identity }
  if (isDemo) {
    sessionStorage.setItem('dev_identity_data', JSON.stringify(updated))
  }
  sessionStorage.setItem(`dev_identity_data_${sessionId}`, JSON.stringify(updated))
}

/** Gọi /webview/init để lấy token + URL SDK FPT cho bước eKYC. */
export async function initWebviewSession(
  sessionId: string,
  payload: Record<string, unknown> = {},
): Promise<Record<string, unknown>> {
  const token = frontendConfig().middlewareWebviewToken
  if (!token) {
    throw new SigningApiError('Thiếu token webview để khởi tạo phiên ký.', 'MISSING_TOKEN', 401)
  }

  const isDemo = sessionId === DEMO_SESSION_ID
  const email = String(payload['email'] || (isDemo ? 'user001@test.com' : ''))
  const phone = String(payload['phone'] || payload['phone_number'] || payload['sdt'] || '')
  const bankName = String(payload['bank_name'] || payload['bank'] || '')
  const accountNumber = String(payload['bank_account'] || payload['account_number'] || '')
  const accountName = String(payload['account_name'] || payload['bank_account_name'] || '')
  const bankBranch = String(payload['bank_branch'] || payload['branch'] || '')

  const initPayload = {
    email,
    phone,
    phone_number: phone,
    ...(payload['full_name'] ? { full_name: String(payload['full_name']) } : {}),
    ...((bankName || accountNumber)
      ? {
          payment_info: {
            bank_name: bankName,
            bank: bankName,
            account_number: accountNumber,
            account_name: accountName,
            bank_branch: bankBranch,
            branch: bankBranch,
          },
          bank_name: bankName,
          bank: bankName,
          account_number: accountNumber,
          account_name: accountName,
          bank_branch: bankBranch,
          branch: bankBranch,
        }
      : {}),
  }

  const res: any = await middlewareApi.initWebview(sessionId, initPayload, { token })
  persistEkycSdkFromInit(sessionId, res?.data, {
    ...payload,
    email,
    phone,
    phone_number: phone,
    ...(bankName ? { bank_name: bankName, bank: bankName } : {}),
    ...(accountNumber ? { bank_account: accountNumber, account_number: accountNumber } : {}),
    ...(accountName ? { account_name: accountName, bank_account_name: accountName } : {}),
    ...(bankBranch ? { bank_branch: bankBranch, branch: bankBranch } : {}),
    ...(payload['full_name'] ? { full_name: String(payload['full_name']) } : {}),
  })
  return (res?.data ?? {}) as Record<string, unknown>
}

export async function submitStep(
  sessionId: string,
  type: StepType,
  payload: Record<string, unknown>,
): Promise<StepResult> {
  const token = frontendConfig().middlewareWebviewToken

  // Nếu phiên ký có cấu hình middlewareWebviewToken, thực hiện gọi trực tiếp qua EKYC Middleware Webview API
  if (token) {
    const options = { token }
    let res: any

    if (type === 'info' || type === 'link_account') {
      const data = await initWebviewSession(sessionId, payload)
      return StepResult.parse({
        ok: true,
        next_step: 'ekyc',
        status: 'in_progress',
        data: {
          ...payload,
          ...data,
        },
      })
    } else if (type === 'ekyc') {
      const rawFront = payload['front_image_url'] || payload['front_image'] || payload['frontSide']
      const rawBack = payload['back_image_url'] || payload['back_image'] || payload['backSide']
      const rawFace = payload['selfie'] || payload['face_image_url'] || payload['faceImage']

      // Upload ảnh thật lên API /api/v1/webview/upload để lấy link URL thật
      const [frontImgUrl, backImgUrl, faceImgUrl] = await Promise.all([
        resolveImageUrl(rawFront, 'front.jpg', options, 'https://cdn.oneat.org/ekyc/front.jpg'),
        resolveImageUrl(rawBack, 'back.jpg', options, 'https://cdn.oneat.org/ekyc/back.jpg'),
        resolveImageUrl(rawFace, 'face.jpg', options, 'https://cdn.oneat.org/ekyc/face.jpg'),
      ])

      const isDemo = sessionId === DEMO_SESSION_ID
      const prevIdentityStr =
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem(`dev_identity_data_${sessionId}`) ||
            (isDemo ? sessionStorage.getItem('dev_identity_data') : null)
          : null
      let prevIdentity: Record<string, any> = {}
      if (prevIdentityStr) {
        try {
          prevIdentity = JSON.parse(prevIdentityStr)
        } catch {
          // ignore
        }
      }

      const idNo = String(
        payload['id_number'] ??
        payload['id_no'] ??
        payload['identification_number'] ??
        payload['soCccd'] ??
        payload['soGTo'] ??
        prevIdentity.id_no ??
        prevIdentity.id_number ??
        ''
      ).trim()

      const fullName = String(
        payload['full_name'] ??
        payload['name'] ??
        payload['hoTen'] ??
        prevIdentity.full_name ??
        prevIdentity.name ??
        ''
      ).trim()

      const dob = String(
        payload['date_of_birth'] ??
        payload['dob'] ??
        payload['ngaySinh'] ??
        prevIdentity.dob ??
        prevIdentity.date_of_birth ??
        ''
      ).trim()

      const issueDate = String(
        payload['issue_date'] ??
        payload['id_issued_date'] ??
        payload['ngay_cap'] ??
        payload['ngayCap'] ??
        prevIdentity.id_issued_date ??
        prevIdentity.issue_date ??
        ''
      ).trim()

      const issuePlace = String(
        payload['issue_place'] ??
        payload['id_issued_place'] ??
        payload['noi_cap'] ??
        payload['noiCap'] ??
        prevIdentity.id_issued_place ??
        prevIdentity.issue_place ??
        (idNo.length === 12 ? 'CỤC CẢNH SÁT QUẢN LÝ HÀNH CHÍNH VỀ TRẬT TỰ XÃ HỘI' : 'CÔNG AN TỈNH')
      ).trim()

      const address = String(
        payload['address'] ??
        payload['permanent_address'] ??
        payload['dia_chi_thuong_tru'] ??
        payload['diaChi'] ??
        payload['noiThuongTru'] ??
        prevIdentity.address ??
        prevIdentity.permanent_address ??
        ''
      ).trim()

      const phone = String(
        payload['phone'] ??
        payload['phone_number'] ??
        payload['sdt'] ??
        prevIdentity.phone ??
        prevIdentity.phone_number ??
        ''
      ).trim()

      const bankName = String(
        payload['bank_name'] ??
        payload['bank'] ??
        prevIdentity.bank_name ??
        prevIdentity.bank ??
        ''
      ).trim()

      const accountNumber = String(
        payload['account_number'] ??
        payload['bank_account'] ??
        prevIdentity.account_number ??
        prevIdentity.bank_account ??
        ''
      ).trim()

      const accountName = String(
        payload['account_name'] ??
        payload['bank_account_name'] ??
        prevIdentity.account_name ??
        prevIdentity.bank_account_name ??
        fullName
      ).trim()

      const bankBranch = String(
        payload['bank_branch'] ??
        payload['branch'] ??
        prevIdentity.bank_branch ??
        prevIdentity.branch ??
        ''
      ).trim()

      const email = String(payload['email'] ?? prevIdentity.email ?? '').trim()

      if (typeof sessionStorage !== 'undefined') {
        const updated = {
          ...prevIdentity,
          ...payload,
          name: fullName,
          full_name: fullName,
          email: email,
          phone: phone,
          phone_number: phone,
          permanent_address: address,
          address: address,
          date_of_birth: dob,
          dob: dob,
          identity_card: idNo,
          id_no: idNo,
          id_number: idNo,
          issue_date: issueDate,
          id_issued_date: issueDate,
          issue_place: issuePlace,
          id_issued_place: issuePlace,
          tax_code: idNo,
          tax_number: idNo,
          mst: idNo,
          ma_so_thue: idNo,
          account_name: accountName,
          account_number: accountNumber,
          bank_name: bankName,
          bank: bankName,
          bank_branch: bankBranch,
          branch: bankBranch,
          ngay_cap: issueDate,
          noi_cap: issuePlace,
          dia_chi_thuong_tru: address,
          resident_address: address,
          payment_info: {
            bank_name: bankName,
            bank: bankName,
            account_number: accountNumber,
            account_name: accountName,
            branch: bankBranch,
            bank_branch: bankBranch,
          },
          front_image_url: frontImgUrl,
          back_image_url: backImgUrl,
          document_images: [frontImgUrl, backImgUrl],
          selfie: faceImgUrl,
        }
        if (isDemo) {
          sessionStorage.setItem('dev_identity_data', JSON.stringify(updated))
        }
        sessionStorage.setItem(`dev_identity_data_${sessionId}`, JSON.stringify(updated))
      }

      // 1. Nộp thông tin giấy tờ CCCD (submit-identity)
      const resolvedAccountName = accountName || fullName
      const identityFields = {
        name: fullName,
        email,
        phone,
        permanent_address: address,
        date_of_birth: dob,
        identity_card: idNo,
        issue_date: issueDate,
        issue_place: issuePlace,
        account_name: resolvedAccountName,
        account_number: accountNumber,
        bank_name: bankName,
        bank_branch: bankBranch,
        tax_code: idNo,
        id_number: idNo,
        id_no: idNo,
        full_name: fullName,
        dob,
        address,
        resident_address: address,
        dia_chi_thuong_tru: address,
        id_issued_date: issueDate,
        ngay_cap: issueDate,
        id_issued_place: issuePlace,
        noi_cap: issuePlace,
        tax_number: idNo,
        mst: idNo,
        ma_so_thue: idNo,
        phone_number: phone,
        branch: bankBranch,
        bank: bankName,
        payment_info: {
          bank_name: bankName,
          bank: bankName,
          account_number: accountNumber,
          account_name: resolvedAccountName,
          branch: bankBranch,
          bank_branch: bankBranch,
        },
      }
      await middlewareApi.submitIdentity(sessionId, {
        ...identityFields,
        front_image_url: frontImgUrl,
        back_image_url: backImgUrl,
      }, options)

      // 2. Nộp thông tin khuôn mặt (submit-face)
      const faceData = {
        face_image_url: faceImgUrl,
        ...(payload['liveness_video_url'] && sanitizeImageUrl(payload['liveness_video_url'], '')
          ? { liveness_video_url: String(payload['liveness_video_url']) }
          : {}),
      }
      await middlewareApi.submitFace(sessionId, faceData, options)

      // 3. Gửi kết quả EKYC tổng hợp (submit-ekyc-result)
      const coreResult = {
        tx_id: String(payload['tx_id'] ?? `CORE_TX_${Date.now()}`),
        status: String(payload['status'] ?? 'completed'),
        identity_verified: true,
        face_matched: true,
        liveness_passed: true,
        score: 98.5,
        identity_data: identityFields,
        ...identityFields,
      }
      res = await middlewareApi.submitEkycResult(sessionId, coreResult, options)
    }

    if (res && res.status === 'success') {
      const data = res.data ?? {}
      const fallbackNext: Partial<Record<StepType, StepType | null>> = {
        info: 'ekyc',
        ekyc: null,
        review: null,
        sign: null,
      }
      const nextStep = type === 'ekyc' ? null : (data.next_step ?? fallbackNext[type] ?? null)
      return StepResult.parse({
        ok: true,
        next_step: nextStep,
        status: nextStep === null ? 'signed' : 'in_progress',
        data,
      })
    }
  }

  // The middleware FE contract has no legacy `/steps/:step` endpoint. For the
  // local hardcoded session, initialize through the documented webview API.
  if (import.meta.env.DEV && sessionId === DEMO_SESSION_ID && type === 'info') {
    if (token) await middlewareApi.initWebview(sessionId, { token })
    return StepResult.parse({ ok: true, next_step: 'ekyc', status: 'in_progress', data: payload })
  }

  if (import.meta.env.DEV && sessionId === DEMO_SESSION_ID) {
    const next: Partial<Record<StepType, StepType | null>> = {
      info: 'ekyc',
      ekyc: null,
      review: null,
      sign: null,
    }
    if (type === 'ekyc' || type === 'sign') markDemoSigned()
    return StepResult.parse({
      ok: true,
      next_step: next[type] ?? null,
      status: (type === 'ekyc' || type === 'sign') ? 'signed' : 'in_progress',
      data: payload,
    })
  }

  const path = expandPath(frontendConfig().stepPath, { sessionId, step: type })
  return StepResult.parse(await request(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  }))
}
